import * as Yup from "yup";
import { Link } from "react-router-dom";
import { useLocation } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Form, Modal, Button, Container } from "react-bootstrap";
import {
  getWebsitesForTabs,
  duplicateCustomerWebTemplate
} from "../../services/api-declaration";
import { ALL_DOTS_REG_PATTERN, THUMBNAIL_TYPE } from "../strings";
import {
  isRefusedReservedTabRoute,
  loadTargetTabRoutes
} from "../../services/website-legal-pages";
import TextField from "../reusable-form-fields/text-field";
import FileInput from "../reusable-form-fields/input-file";
import WebsiteReplaceDialog from "./website-replace-dialog";
import SpinnerButton from "../spinner-button/spinner-button";
import { useOptionsFetch } from "../../hooks/useOptionsFetch";
import { useNotification } from "../../hooks/useNotification";
import SelectField from "../reusable-form-fields/select-field";
import { showSuccess, showWarning } from "../../services/toast-service";
import ValidationMessage from "../validation-message/validation-message";
import AsyncCreatableSelectField from "../reusable-form-fields/creatable-async-select";
import DuplicateWebsiteDomainSection from "../reusable-form-section/duplicate-website-domain-section";
import { useTranslation } from "react-i18next";

const WebsiteDuplicateDialog = ({
  show,
  // mainFormValues,
  toggleDialog,
  customerId,
  tabRoute
  // templateType
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { state } = useLocation();
  const [isDomainValid, setIsDomainValid] = useState(true);
  const btnRef = useRef(null);
  const { showToastWithLink } = useNotification();
  const [replaceTemplateData, setReplaceTemplateData] = useState(null);
  const [showreplaceDialog, setShowReplaceDialog] = useState(false);
  const [shouldAllowNestedTab, setShouldAllowNestedTab] = useState(true);
  // Active tab routes of the selected (target) customer, read by the tabRoute
  // rule; null while not loaded (the backend then decides). Only the answer
  // for the last selected customer is kept.
  const targetTabRoutesRef = useRef(null);
  const tabRoutesRequestRef = useRef(0);

  const logoTypes = [
    { name: t("customerWebsite.text"), _id: "text" },
    { name: t("customerWebsite.image"), _id: "image" }
  ];
  const { REACT_APP_ASSET_CDN_URL } = process.env;

  const toggleReplaceDialog = () => {
    setShowReplaceDialog((prev) => !prev);
  };

  const displayLinkToast = ({ customerId, tabRoute, toastType, toastMsg }) =>
    showToastWithLink({
      url: `/dashboard/customers/${customerId}/website${tabRoute}`,
      msg: toastMsg,
      showToast: toastType
    });

  const { handleCustomerOptions } = useOptionsFetch();

  const parseResourceSrc = (resourceObj) =>
    resourceObj?.key
      ? `${REACT_APP_ASSET_CDN_URL}/${resourceObj.key}/${THUMBNAIL_TYPE.ANSWER_OPTION_ICON}`
      : "";
  const parsePreviewResourceSrc = (resourceObj) =>
    resourceObj?.key ? `${REACT_APP_ASSET_CDN_URL}/${resourceObj.key}` : "";

  const onSubmit = async (formVals) => {
    setIsSubmitting(true);
    const selectedCustomerId = formVals.customer._id;
    const addedTabRoute = formVals.tabRoute;

    const template = {
      domain: formVals.domain,
      logo: formVals.logo,
      tabRoute: addedTabRoute,
      cloneFromCustomer: customerId,
      cloneFromTab: tabRoute
    };

    try {
      // Send API request
      await duplicateCustomerWebTemplate(selectedCustomerId, template);

      displayLinkToast({
        customerId: selectedCustomerId,
        tabRoute: addedTabRoute,
        toastType: showSuccess,
        toastMsg: "Website Cloned Successfully"
      });
      toggleDialog();
    } catch (error) {
      console.error("Error submitting form:", error);
      showWarning(error.response.data.message);
      if (error.response.status === 409) {
        setReplaceTemplateData({
          template,
          customerId: selectedCustomerId,
          tabRoute: addedTabRoute,
          templateId: error.response.data.data.templateId
        });
        toggleReplaceDialog();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const YupCustomerSelectFieldValidation = Yup.lazy((value) => {
    if (value) {
      switch (typeof value) {
        case "string":
          return Yup.string().required("Required").nullable();
        case "object":
          return Yup.object().shape({
            _id: Yup.string()
              .required("Required")
              .nullable()
              .test(
                "not-equal-customerId",
                "Please Select Different Customer",
                (id) => id !== customerId
              )
          });
        default:
          return Yup.mixed();
      }
    } else {
      return Yup.string().required("Required").nullable();
    }
  });

  const duplicateWebsiteSchema = Yup.object().shape({
    customer: YupCustomerSelectFieldValidation,
    domain: Yup.string()
      .required("Domain is required")
      .matches(
        /^(?!https?:\/\/)/,
        "Protocol not allowed. Remove http:// or https://"
      )
      .matches(/^(?!www\.)/, 'Remove "www." from the domain')
      .matches(
        /^[a-z0-9.-]+$/,
        "Only lowercase letters, numbers, dot, and hyphen are allowed"
      )
      .matches(
        /^[^!@#$%^&*()_+={}[\]:;"'<>,?/|\\]+$/,
        "Special characters are not allowed"
      )
      .matches(/^(?!.*\.\.)/, "Consecutive dots are not allowed")
      .test("two-dots", "Only two dots are allowed", (value) =>
        value
          ? (value.match(ALL_DOTS_REG_PATTERN) || []).length === 1 ||
            (value.match(ALL_DOTS_REG_PATTERN) || []).length === 2
          : false
      )
      .matches(/^[^.].*[^.]$/, "Dot cannot be at the start or end")
      .max(63, "Domain must be at most 63 characters long"),
    logo: Yup.object().shape({
      type: Yup.object().shape({
        name: Yup.string().required("Type is required"),
        _id: Yup.string().required("Type is required")
      }),
      text: Yup.string().when("type", {
        is: (val) => val?._id === logoTypes[0]?._id,
        then: (schema) => schema.required("Logo text is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      image: Yup.mixed().when("type", {
        is: (val) => val?._id === logoTypes[1]?._id,
        then: (schema) => schema.required("Logo image is required"),
        otherwise: (schema) => schema.notRequired()
      })
    }),
    tabRoute: Yup.string()
      .required("Required")
      .matches(
        /^\/(?:[a-zA-Z]+(?:-[a-zA-Z]+)*)?$/,
        "Enter a valid route (example: /about-us)"
      )
      .test(
        "reserved-tab-route",
        t("customerWebsite.reservedTabRoute"),
        (value) => !isRefusedReservedTabRoute(value, targetTabRoutesRef.current)
      )
  });

  const methods = useForm({
    resolver: yupResolver(duplicateWebsiteSchema)
  });
  const {
    register,
    handleSubmit,
    getValues,
    watch,
    control,
    setValue,
    formState: { errors }
  } = methods;

  const selectedCustomerId = getValues("customer")?._id;

  const logoType = watch("logo.type");
  const logoImage = watch("logo.image");

  const getSelectedCustomerWebTabs = (_customerId) => {
    targetTabRoutesRef.current = null;
    return loadTargetTabRoutes({
      customerId: _customerId,
      requestRef: tabRoutesRequestRef,
      fetchTabRoutes: (id) => getWebsitesForTabs(id, { active: true }),
      onLoaded: (tabRoutes) => {
        targetTabRoutesRef.current = tabRoutes;
        setShouldAllowNestedTab(Boolean(tabRoutes.length));
        setValue("tabRoute", "/");
      },
      onFailed: (error) => {
        console.log("Error in getSelectedCustomerWebTabs ", error);
        // Nothing of the previous customer stays; the backend decides
        setShouldAllowNestedTab(true);
        setValue("tabRoute", "/");
        showWarning(t("customerWebsite.tabRoutesLoadFailed"));
      }
    });
  };

  const getSelectedCustomerWebTabsCb = useCallback(
    getSelectedCustomerWebTabs,
    // eslint-disable-next-line
    []
  );

  useEffect(() => {
    if (selectedCustomerId) {
      getSelectedCustomerWebTabsCb(selectedCustomerId);
    }
  }, [selectedCustomerId, getSelectedCustomerWebTabsCb]);

  // An answer that arrives after the dialog closed is ignored
  useEffect(
    () => () => {
      tabRoutesRequestRef.current += 1;
    },
    []
  );

  return (
    <>
      <Modal size="lg" show={show} onHide={toggleDialog} centered>
        <Modal.Header className="bg-success text-white">
          <h5 className="m-0">{t("customerWebsite.cloneDialogHeading")}</h5>
        </Modal.Header>
        <Modal.Body>
          <FormProvider {...methods} id="template-form">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Container className="px-0">
                <Row className="mb-3">
                  <Col sm={12} md={12}>
                    <div className="d-flex align-items-end">
                      <Form.Group controlId="customer" className="flex-fill">
                        <Form.Label
                          className={errors?.customer && "text-danger"}
                        >
                          {t("common.customer")}
                          {errors?.customer && "*"}
                        </Form.Label>
                        <div className="d-flex align-items-center">
                          <AsyncCreatableSelectField
                            control={control}
                            name="customer"
                            getOptionLabel={(opt) =>
                              `${opt.firstName} ${opt.lastName} (${opt.email})`
                            }
                            fetchData={handleCustomerOptions}
                            isDisabled={state?.navigatedCustomer}
                            capitalized={false}
                          />
                        </div>
                        <ValidationMessage
                          error={
                            errors?.customer?.message ||
                            errors?.customer?._id?.message
                          }
                        />
                      </Form.Group>
                      {watch("customer")?._id && (
                        <Button
                          as={Link}
                          target="_blank"
                          className="mb-1 ms-3"
                          variant="outline-secondary"
                          to={`../../../customers/${
                            getValues("customer")._id
                          }/overview`}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
                <DuplicateWebsiteDomainSection
                  btnRef={btnRef}
                  isDomainValid={isDomainValid}
                  setIsDomainValid={setIsDomainValid}
                  inDialog
                  noHeading
                  customerId={customerId}
                  selectedCustomerId={getValues("customer")?._id}
                  // setShouldUpdateNonActive={setShouldUpdateNonActive}
                />

                <Row className="mt-3">
                  <Col xs={12} md={6}>
                    <TextField
                      name="tabRoute"
                      defaultValue="/"
                      type="text"
                      label={"Tab Route:"}
                      placeholder={"Enter Tab Route"}
                      errors={errors?.tabRoute}
                      register={register}
                      noSpacing
                      disabled={!shouldAllowNestedTab}
                    />
                  </Col>
                  {!shouldAllowNestedTab && (
                    <div className="small text-primary fw-bold">
                      This customer does not have any Base Website
                    </div>
                  )}
                </Row>

                <Row className="mt-3">
                  <Col xs={12} md={6}>
                    <Form.Group controlId="land">
                      <Form.Label>
                        {t("customerWebsite.logo.logoType")}
                      </Form.Label>
                      <SelectField
                        control={control}
                        name="logo.type"
                        options={logoTypes}
                        getValues={getValues}
                        error={errors?.logo?.type?.name?.message}
                      />
                    </Form.Group>
                  </Col>

                  {logoType?._id === logoTypes[0]._id ? (
                    <>
                      <Col sm={12} md={6}>
                        <TextField
                          label={t("customerWebsite.logo.logoText")}
                          placeholder={t("customerWebsite.logo.logoText")}
                          type="text"
                          name="logo.text"
                          errors={errors?.logo?.text}
                          register={register}
                          noSpacing
                        />
                      </Col>
                    </>
                  ) : logoType?._id === logoTypes[1]._id ? (
                    <Col xs={12} md={6}>
                      <FileInput
                        label={t("customerWebsite.logo.logoImage")}
                        name="logo.image"
                        // resourceId={resourceId}
                        parentFolderName={"customer"}
                        url={parseResourceSrc(logoImage)}
                        urlForPreview={parsePreviewResourceSrc(logoImage)}
                        asset={logoImage?.asset}
                        error={errors?.logo?.image}
                        // selectedCategory={getValues("category")}
                        // tagName="backgroundimage"
                      />
                    </Col>
                  ) : null}
                </Row>
              </Container>
              <hr />
              <div className="d-flex flex-row-reverse">
                <Button
                  className="ms-2"
                  variant="outline-secondary"
                  onClick={() => toggleDialog()}
                >
                  {t("common.cancel")}
                </Button>
                {isSubmitting ? (
                  <SpinnerButton
                    btnText="Saving..."
                    btnStyle="btn-outline-success"
                  />
                ) : (
                  <Button
                    ref={btnRef}
                    type="submit"
                    variant="outline-success"
                    disabled={!isDomainValid}
                  >
                    {t("common.save")}
                  </Button>
                )}
              </div>
            </Form>
          </FormProvider>
        </Modal.Body>
      </Modal>

      {showreplaceDialog && (
        <WebsiteReplaceDialog
          show={showreplaceDialog}
          toggleDuplicateDialog={toggleDialog}
          toggleDialog={toggleReplaceDialog}
          data={replaceTemplateData}
        />
      )}
    </>
  );
};
export default WebsiteDuplicateDialog;
